import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SearchCountryField, CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input';
import { CustomValidator } from 'src/app/helpers/custom.validator';
import { Patterns } from 'src/app/helpers/patterns';
import { LabDetails } from 'src/app/shared/models/labs/LabDetails';
import { LabForm } from 'src/app/shared/models/labs/LabForm';
import { LabType } from 'src/app/shared/models/labs/enums/LabType';
import { Lookup } from 'src/app/shared/models/lookups/Lookup';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { LabsService } from 'src/app/shared/services/labs.service';
import { LookupsService } from 'src/app/shared/services/lookups.service';
import { SwalService } from 'src/app/shared/services/swal.service';
export interface DialogData {
  recordId: number;
  record: LabDetails;
  addBranch: boolean;
  isOurProfile: boolean;
  isOurProfileVl: boolean;
}

@Component({
  selector: 'app-lab-form',
  templateUrl: './lab-form.component.html'
})
export class LabFormComponent implements OnInit {
  dataLoading = false;
  loading = false;
  hidePassword = true;

  recordId!: number;
  isOurProfile!: boolean;
  isOurProfileVl!: boolean;
  details!: LabDetails;
  disableLabType = false;

  formGroup!: FormGroup;
  isSubmitted = false;

  entityList: any[] = [];
  mainLabsList: Lookup[] = [];
  countryList: Lookup[] = [];
  regionList: Lookup[] = [];
  cityList: Lookup[] = [];
  scanningCentersList: Lookup[] = [];

  // image file
  logo: any;
  logoSrc!: any;
  maxFileSize = 2;
  fileTypeError = false;
  fileSizeError = false;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  selectedCountryISO: any = CountryISO.SaudiArabia;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef: MatDialogRef<LabFormComponent>,
    private formBuilder: FormBuilder,
    private service: LabsService,
    private lookupsService: LookupsService,
    private auth: AuthenticationService,
    private swalService: SwalService,
    private translate: TranslateService,
    private el: ElementRef
  ) { }

  ngOnInit(): void {
    this.buildForm();

    if (this.data.isOurProfile) {
      this.isOurProfile = this.data.isOurProfile;
      this.bindDataToUpdate();
    } else if (this.data.isOurProfileVl) {
      this.isOurProfileVl = this.data.isOurProfileVl;
      this.bindDataToUpdate();
    } else {
      if (this.data && this.data.record && this.data.record.id) {
        this.recordId = this.data.recordId;
        this.bindDataToUpdate();
      }
    }

    if (this.data && this.data.addBranch) {
      this.formGroup.controls['type'].setValue(LabType.BRANCH);
      this.formGroup.controls['mainLab'].setValue(this.data.recordId);
    }

    if (this.data && this.data.record && (
      this.data.addBranch // Add new branche
      || (this.data.record.branches && this.data.record.branches.length) // Has branches
      || this.data.record.casesCount
    )) {
      this.disableLabType = true;
    }
    this.getAllMain();
    this.getCountries();
  }

  getDetails() {
    this.dataLoading = true;
    this.service.getDetails(this.recordId).subscribe(
      (data: any) => {
        this.details = data as LabDetails;
        this.dataLoading = false;
        this.bindDataToUpdate();
      },
      (error: any) => {
        this.dataLoading = false;
      });
  }

  buildForm() {
    this.formGroup = this.formBuilder.group({
      type: [LabType.MAINLAB],
      entityId: [null],
      mainLab: [null],
      Lab: [null, CustomValidator.noWhiteSpace],
      contactNumber: [null, Validators.required],
      email: [null, [
        Validators.required,
        Validators.email,
        Validators.pattern(Patterns.email),
      ]],
      country: [null, Validators.required],
      region: [null, Validators.required],
      cityId: [null, Validators.required],
      address: [null, CustomValidator.noWhiteSpace]
    });
  }

  hasError(controlName: string, errorName: string, index = null) {
    return this.formGroup.controls[controlName].hasError(errorName);
  }

  getAllMain() {
    this.service.getAllMain(this.recordId).subscribe(
      (data: any) => {
        this.mainLabsList = data as [];
      }, (err: any) => {
        console.error(err);
      });
  }
  getCountries() {
    this.lookupsService.getCountries().subscribe(
      (data: any) => {
        this.countryList = data as [];
        if (!this.recordId && !this.isOurProfile && !this.isOurProfileVl) {
          this.regionList = [];
          this.cityList = [];
          this.formGroup.controls['country'].setValue(this.countryList.find(item => item.isDefault)?.id);
          this.getRegions();
        }
      }, (err: any) => {
        console.error(err);
      });
  }
  getRegions(changed = false) {
    this.lookupsService.getRegions(this.formGroup.controls['country'].value).subscribe(
      (data: any) => {
        this.regionList = data as [];
        if (changed) {
          this.cityList = [];
          this.formGroup.controls['region'].setValue(null);
          this.formGroup.controls['cityId'].setValue(null);
        }
      }, (err: any) => {
        console.error(err);
      });
  }
  getCities(changed = false) {

    this.lookupsService.getCities(
      this.formGroup.controls['country'].value,
      this.formGroup.controls['region'].value).subscribe(
        (data: any) => {
          this.cityList = data as [];
          if (changed) {
            this.formGroup.controls['cityId'].setValue(null);
          }
        }, (err: any) => {
          console.error(err);
        });
  }

  bindDataToUpdate(record = this.data.record) {
    this.formGroup.controls['type'].setValue(record.entity ? LabType.MAINLAB : record.labType);
    this.formGroup.controls['mainLab'].setValue(record.mainLab ? record.mainLab.id : null);
    this.formGroup.controls['Lab'].setValue(record.labName);
    if (record.logo && record.logo) { this.logoSrc = record.logo; }
    this.formGroup.controls['contactNumber'].setValue(record.basicInfo.contactNumber);
    this.selectedCountryISO = record.basicInfo.contactNumberFlag;
    this.formGroup.controls['email'].setValue(record.basicInfo.email);
    this.formGroup.controls['country'].setValue(record.country.id);
    this.formGroup.controls['region'].setValue(record.region.id);
    this.formGroup.controls['cityId'].setValue(record.city.id);
    this.formGroup.controls['address'].setValue(record.basicInfo.address);
    this.getRegions();
    this.getCities();
  }

  bindMatMultiSelect(data: any, parent: any) {
    const selectedList: any = [];
    if (parent && data) {
      parent.forEach((item: any) => {
        if (data.some((obj: any) => obj.id === item.id) || data.some((obj: any) => obj.id === item.roleId)) {
          selectedList.push(item);
        }
      });
    }
    return selectedList;
  }

  saveRecord() {
    this.isSubmitted = true;
    if (this.formGroup.valid && this.logoSrc) {
      this.loading = true;
      let data: LabForm;
      data = {
        lab: this.formGroup.controls['Lab'].value,
        ContactNumberFlag: this.formGroup.controls['contactNumber'].value.countryCode,
        ContactNumberExtension: this.formGroup.controls['contactNumber'].value.dialCode,
        ContactNumber: this.formGroup.controls['contactNumber'].value.number,
        email: this.formGroup.controls['email'].value,
        cityId: this.formGroup.controls['cityId'].value,
        streetaddress: this.formGroup.controls['address'].value,
      } as LabForm;
      if (!this.isOurProfileVl) {
        data.type = this.formGroup.controls['type'].value;
        data.mainLab = this.formGroup.controls['mainLab'].value;
      }
      if (this.formGroup.controls['type'].value === LabType.MAINLAB) {
        data.mainLab = null;
      }

      if (this.recordId) { data.labId = this.recordId; }
      if (this.isOurProfile || this.isOurProfileVl) {
        const type = this.isOurProfileVl ? 'profileVl' : null;
        this.service.updateLabProfile(data, this.logo, type).subscribe(() => {
          this.loading = false;
          this.swalService.swalSuccess(this.translate.instant('swalMsg.updatedsuccessfully'));
          this.dialogRef.close(true);
        }, (err: any) => {
          this.loading = false;
          this.handleErrors(err.detail);
          this.scrollToFirstInvalidControl();
        });
      } else {
        this.service.saveRecord(data, this.logo).subscribe(() => {
          this.loading = false;
          this.swalService.swalSuccess(this.recordId ? this.translate.instant('swalMsg.updatedsuccessfully') : this.translate.instant('swalMsg.savedsuccessfully'));
          this.dialogRef.close(true);
        }, (err: any) => {
          this.loading = false;
          this.handleErrors(err.detail);
          this.scrollToFirstInvalidControl();
        });
      }
    } else {
      this.formGroup.markAllAsTouched();
      this.scrollToFirstInvalidControl();
    }
  }

  handleErrors(errors: any) {
    errors = JSON.parse(errors);
    errors.ErrorDTO.forEach((error: any) => {
      if (error.ErrorCode === 'Repeated') {
        this.formGroup.controls[error.ErrorDetail].setErrors({ notUniqe: true });
      }
    });
  }

  // File preview with validation
  changeFileListener($event: any): void {
    const file: File = $event.target.files[0];
    if (file && this.validateFile(file)) {
      this.readThis(file);
      this.logo = file;
    } else {
      this.logoSrc = undefined;
      this.logo = undefined;
    }
  }
  readThis(file: File): void {
    const myReader: FileReader = new FileReader();
    myReader.readAsDataURL(file);
    myReader.onloadend = (e) => {
      this.logoSrc = myReader.result as string;
    };
  }
  validateFile(file: File): any {
    if (this.fileType(file.name)) {
      this.fileTypeError = false;
      if ((file.size / (1024 * 1024)) <= this.maxFileSize) {
        this.fileSizeError = false;
      } else {
        this.fileSizeError = true;
        return false;
      }
    } else {
      this.fileTypeError = true;
      return false;
    }
    return true;
  }
  fileType(fileName: any): any {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'jpeg':
      case 'jpg':
        return 'jpg';
      case 'png':
        return 'png';
      default:
        return false;
    }
  }

  changeSlide(e: any) {
    if (e.checked) {
      this.formGroup.controls['type'].setValue(LabType.MAINLAB);
    }
  }

  hasPermission(permission: string) {
    return this.auth.hasPermission(permission);
  }

  autoFillInput(controlName: string) {
    this.formGroup.controls[controlName].reset();
  }

  private scrollToFirstInvalidControl() {
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector(
      'form .ng-invalid'
    );
    console.log(firstInvalidControl)
    firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }
}
